import { GridColumns } from '@mui/x-data-grid-pro'
import { Context, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  useDeleteDeviationFollowUp,
  invalidateDeviation,
  useDeleteDeviationFollowUpAdmin,
} from '../../../api/deviation'
import { CANNOT_DELETE } from '../../../constant'
import { IDeviation } from '../../../generated-types/deviation'
import { IDeviationPriority } from '../../../generated-types/deviation-priority'
import { AuthorizationProvider } from '../../../components/common/Authorization'
import { permissionValue } from '../../../enums/permissionValue'
import { AuthorizationType } from '../../../enums/auth'
import GenericTable from 'src/components/table/GenericTable'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DeleteOutline, WarningAmberRounded } from '@mui/icons-material'
import { MUIThemeColors } from 'src/mui-theme'
import { Chip } from 'src/components/common/Chip'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { Box, Typography, useTheme } from '@mui/material'
import { getLocalDateTime } from 'src/utils/function'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { priorityColorToken, statusColorToken } from './detail/deviationStatus'
import { DeviationStatus } from 'src/enums'

export const DeviationFollowUpTableComponent: React.FC<{
  deviation: IDeviation
  handleDelete: (id: EntityId) => void
  context?: Context<any> | null
}> = ({ deviation, handleDelete, context }) => {
  const theme = useTheme()

  const { t } = useTranslation(['deviation', 'common'])
  const columns: GridColumns = [
    {
      field: 'description',
      headerName: t('description', { ns: 'common' }),
      flex: 2.6,
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: 1.4,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            py: 0.75,
          }}
        >
          {row?.description}
        </Typography>
      ),
    },
    {
      field: 'performedBy',
      headerName: t('performedBy', { ns: 'common' }),
      flex: 0.8,
    },
    {
      field: 'priority',
      headerName: t('priority', { ns: 'common' }),
      flex: 0.6,
      renderCell: value =>
        value.value == IDeviationPriority.Critical ? (
          <Chip
            label={t('critical', { ns: 'common' })}
            variant="outlined"
            sx={{
              borderColor: getThemeColor(theme, priorityColorToken(IDeviationPriority.Critical)),
              color: `${getThemeColor(theme, priorityColorToken(IDeviationPriority.Critical))} !important`,
              height: '24px',
              borderRadius: '4px',
            }}
          />
        ) : (
          <Chip
            label={t('normal', { ns: 'common' })}
            variant="outlined"
            sx={{
              borderColor: getThemeColor(theme, priorityColorToken(IDeviationPriority.Normal)),
              color: `${getThemeColor(theme, priorityColorToken(IDeviationPriority.Normal))} !important`,
              height: '24px',
              borderRadius: '4px',
            }}
          />
        ),
    },
    {
      field: 'dueDate',
      headerName: t('dueDate', { ns: 'common' }),
      flex: 0.7,
      valueGetter: value => {
        return getFormattedDate(getLocalDateTime(value.value))
      },
    },
    {
      field: 'status',
      headerName: t('status', { ns: 'common' }),
      flex: 0.6,
      renderCell: ({ row }) => {
        return !row?.status ? (
          <Chip
            label={t('open', { ns: 'common' })}
            variant="outlined"
            sx={{
              borderColor: getThemeColor(theme, statusColorToken(DeviationStatus.Open)),
              color: `${getThemeColor(theme, statusColorToken(DeviationStatus.Open))} !important`,
              height: '24px',
              borderRadius: '4px',
            }}
          />
        ) : (
          <Chip
            label={t('closed', { ns: 'common' })}
            variant="outlined"
            sx={{
              borderColor: getThemeColor(theme, statusColorToken(DeviationStatus.Complete)),
              color: `${getThemeColor(theme, statusColorToken(DeviationStatus.Complete))} !important`,
              height: '24px',
              borderRadius: '4px',
            }}
          />
        )
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      renderCell: params => {
        return (
          <AuthorizationProvider
            permissions={[
              permissionValue.Locality.Deviation.Delete,
              permissionValue.ClientAdminPermission,
              permissionValue.ServiceStation.Write,
              permissionValue.ServiceStation.Update,
              permissionValue.Locality.MainComponent.Deviation.Write,
              permissionValue.Locality.MainComponent.Deviation.Delete,
            ]}
            type={AuthorizationType.Disabled}
            data-cy="deleteFollowUp"
            Context={context}
          >
            <GridActionButton
              onClick={e => {
                e.preventDefault()
                handleDelete(params?.id)
              }}
              height="32px"
              width="32px"
              buttonIcon={<DeleteOutline />}
              shadowWeight={0}
              disabled={deviation.status}
            />
          </AuthorizationProvider>
        )
      },
    },
  ]

  const rowCount = deviation.deviationFollowUps?.length ?? 0

  if (!rowCount) {
    return (
      <Box sx={{ px: 2, py: 1.25 }}>
        <Typography sx={{ fontSize: '13px', color: getThemeColor(theme, MUIThemeColors.secondaryMain) }}>
          {t('noFollowUps', { ns: 'deviation' })}
        </Typography>
      </Box>
    )
  }

  // autoHeight + per-row 'auto' lets the grid measure each row, so the full description
  // wraps across as many lines as needed without clipping. The empty case is handled above.
  return (
    <GenericTable
      id="deviation-follow-up-table"
      data={deviation.deviationFollowUps}
      columns={columns}
      autoHeight
      getRowHeight={() => 'auto' as const}
    />
  )
}

export const DeviationFollowUpTable: React.FC<{
  deviation: IDeviation
  context?: Context<any> | null
}> = ({ deviation, context }) => {
  const { customerId, localityId, deviationId } = useParams()
  const { t } = useTranslation(['deviation', 'common'])
  const { mutateAsync, error } = useDeleteDeviationFollowUp(customerId as EntityId, deviationId as EntityId)
  const { showConfirmDialog } = useDialogCtx()

  useEffect(() => {
    if (error && error['data'] == CANNOT_DELETE) {
      toast(t('cannot-delete-followUp'), { type: 'error' })
    }
  }, [error])

  const handleDelete = async (id: EntityId) => {
    showConfirmDialog(
      t('deleteDeviationFollowUp'),
      t('areYouSureDeleteFollowUp'),
      undefined,
      async pop => {
        await mutateAsync(id, {
          onSuccess: async () => {
            toast(t('followUp-deleted-successfully'), { type: 'success' })
            invalidateDeviation.invalidateAllDeviations(
              customerId as EntityId,
              localityId as EntityId,
              undefined,
              deviationId,
            )
            invalidationActivityLogCache.getCageActivityLogs()
            invalidateMainComponentCache.getMainComponents(customerId as EntityId, localityId as EntityId)
            pop()
          },
        })
      },
      {
        iconBGColor: '#E96B874D',
        Icon: <WarningAmberRounded sx={{ fontSize: 48, color: '#E96B87' }} />,
        buttonBGColor: '#E96B87',
        buttonTextColor: MUIThemeColors.white,
      },
    )
  }
  return <DeviationFollowUpTableComponent context={context} deviation={deviation} handleDelete={handleDelete} />
}

export const DeviationFollowUpTableAdmin: React.FC<{
  deviation: IDeviation
  context?: Context<any> | null
}> = ({ deviation, context }) => {
  const { t } = useTranslation(['deviation', 'common'])
  const { mutateAsync, error } = useDeleteDeviationFollowUpAdmin(deviation.id as EntityId)
  const { showConfirmDialog } = useDialogCtx()

  useEffect(() => {
    if (error && error['data'] == CANNOT_DELETE) {
      toast(t('cannot-delete-followUp'), { type: 'error' })
    }
  }, [error])

  const handleDelete = async (id: EntityId) => {
    showConfirmDialog(t('deleteDeviationFollowUp'), t('areYouSureDeleteFollowUp'), undefined, async pop => {
      await mutateAsync(id, {
        onSuccess: async () => {
          toast(t('followUp-deleted-successfully'), { type: 'success' })
          invalidateDeviation.invalidateAllDeviations(undefined, undefined, undefined, id)
          pop()
        },
      })
    })
  }
  return <DeviationFollowUpTableComponent context={context} deviation={deviation} handleDelete={handleDelete} />
}
